// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld/base/core';
import L from 'leaflet';
import _ from 'underscore';
import FilterParser from 'myWorld/base/filterParser';
import {
    GeoserverImgRequest,
    CONNECTION_METHODS,
    GeoserverAuthDefaults
} from './geoserverImgRequest';

myw.GeoserverLayer = L.TileLayer.WMS.extend({
    initialize(url, options) {
        url = url || '';
        options = options || {};

        options.cacheBust = Math.round(Math.random() * 1000000);

        L.TileLayer.WMS.prototype.initialize.call(this, url, options);

        this.startupInfo = options.startupInfo || {};
        this.applicationName = options.applicationName;
        this.ds = options.ds;

        this._predicates = this.createParseTree(options.filters);
    },

    /**
     * @class Define a myWorld specific tile layer for tiles supplied by geoserver.<br/>
     * Prevents browser from caching tiles between sessions
     * Extends {@link http://leafletjs.com/reference.html#tilelayer-wms|L.TileLayer.WMS}
     * @constructs
     */
    getTileUrl(tilePoint) {
        // (Point, Number) -> String
        let sessionVars = myw.app.database.getSessionVars();
        sessionVars = _.extend(
            {
                user: myw.currentUser.username,
                application: this.applicationName,
                roles: this.startupInfo.roles,
                rights: this.startupInfo.rights,
                activeDelta: (this.ds && this.ds.delta) || ''
            },
            sessionVars
        );

        this.wmsParams.layers =
            this.options.wmsLayerGroup ||
            this.options.featureItems
                .filter(featureItem => {
                    if (!featureItem.min_vis && !featureItem.max_vis) return true;

                    const withinMinRange = tilePoint.z >= featureItem.min_vis;
                    const withinMaxRange = tilePoint.z <= featureItem.max_vis;

                    if (featureItem.min_vis && featureItem.max_vis)
                        return withinMinRange && withinMaxRange;
                    if (featureItem.min_vis) return withinMinRange;
                    if (featureItem.max_vis) return withinMaxRange;

                    return false;
                })
                .map(layerItem => (this.options.prefix || '') + layerItem.name);

        if (this._predicates) {
            const cqlFilter = this._getCQLFor(this._predicates, sessionVars);
            this.wmsParams.CQL_FILTER = cqlFilter.join(';');
        }

        return `${L.TileLayer.WMS.prototype.getTileUrl.call(this, tilePoint)}&${
            this.options.cacheBust
        }`;
    },

    // Create a parse tree for the myWorld filters
    createParseTree(filters) {
        if (!filters || !filters.length) return;
        const predicates = filters.map(filter => {
            if (!filter) return '';
            else {
                const filterParser = new FilterParser(filter.value).parse();
                filterParser.layer = filter.layerName;
                return filterParser;
            }
        });
        return predicates;
    },

    redraw() {
        //set new cache bust token to ensure tiles are reloaded by the browser
        this.options.cacheBust = Math.round(Math.random() * 1000000);

        L.TileLayer.WMS.prototype.redraw.call(this);
    },

    _getCQLFor(predicates = [], sessionVars) {
        return predicates.map(predicate => {
            if (predicate) {
                return predicate.sqlFilter(predicate.layer, undefined, sessionVars, 'CQL');
            } else {
                return 'INCLUDE';
            }
        });
    },

    //  Copied and modified from Leaflet.TileLayer
    //  If auth is not required, used original code, else use XMLHTTPRequest
    createTile: function(coords, done) {
        var tile = document.createElement('img');

        if (this.options.crossOrigin) {
            tile.crossOrigin = '';
        }

        /*
		 Alt tag is set to empty string to keep screen readers from reading URL and for compliance reasons
		 http://www.w3.org/TR/WCAG20-TECHS/H67
		*/
        tile.alt = '';

        /*
		 Set role="presentation" to force screen readers to ignore this
		 https://www.w3.org/TR/wai-aria/roles#textalternativecomputation
		*/
        tile.setAttribute('role', 'presentation');

        let authOptions = this.options.auth || GeoserverAuthDefaults;
        const URL = this.getTileUrl(coords);

        if (authOptions.type === CONNECTION_METHODS.NONE) {
            //  No point using the XMLHTTPRequest if no auth is required
            tile.src = URL;
            tile.addEventListener('load', this._tileOnLoad.bind(this, done, tile));
            tile.addEventListener('error', this._tileOnError.bind(this, done, tile));
        } else {
            const request = GeoserverImgRequest(URL, authOptions);
            request.addEventListener('load', () => {
                const reader = new FileReader();
                reader.addEventListener('loadend', () => {
                    tile.src = reader.result;
                    this._tileOnLoad(done, tile);
                });
                reader.readAsDataURL(request.response);
            });
            request.addEventListener('error', () => {
                this._tileOnError(done, tile);
            });
            request.responseType = 'blob';
            request.send();
        }

        return tile;
    }
});

export default myw.GeoserverLayer;
